// Keep the host's URL and our router in step (apps contract v1.1 §2.1).
//
// - We announce every internal navigation with `rumio:location`, so the
//   host mirrors it into its own URL under the mount prefix. Deep links
//   and reloads then land on the same screen.
// - The host sends `rumio:navigate` when the user moves through its
//   history (back/forward) or follows a link to a sub-path; we render that
//   path without announcing it back (echo guard), so history doesn't loop.

type Navigate = (path: string) => void;

function isEmbedded(): boolean {
  return typeof window !== 'undefined' && window.parent !== window;
}

export interface HostNav {
  /** Call on every router location change. */
  announce(path: string): void;
  stop(): void;
}

export function startHostNav(navigate: Navigate): HostNav {
  if (!isEmbedded()) return { announce: () => {}, stop: () => {} };

  let fromHost: string | null = null;
  let last: string | null = null;

  const onMessage = (event: MessageEvent) => {
    if (event.source !== window.parent) return;
    const data = event.data as { type?: unknown; path?: unknown } | null;
    if (data?.type !== 'rumio:navigate' || typeof data.path !== 'string' || !data.path.startsWith('/')) return;
    fromHost = data.path;
    navigate(data.path);
  };
  window.addEventListener('message', onMessage);

  return {
    announce(path) {
      if (path === fromHost) {
        fromHost = null;
        last = path;
        return;
      }
      if (path === last) return;
      last = path;
      window.parent.postMessage({ type: 'rumio:location', path }, '*');
    },
    stop() {
      window.removeEventListener('message', onMessage);
    },
  };
}
